import { observer } from 'mobx-react-lite'
import { Alert } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { useLocalStorage, setLocalStorageItem } from 'shared/hooks/useLocalStorage'
import { ChatbotPath } from 'entities/ExportRouters'
import { IntegrationKey } from 'entities/Integrations'
import { type Conversation } from 'entities/Conversation'
import { useEntitiesContext } from 'entities/EntitiesExport'

export interface IConversationCalendlyAlertProps {
  conversation: Conversation
}
const CALENDLY_ALERT_DISMISS_KEY = 'calendlyAlertDismissDate'

export const ConversationCalendlyAlert = observer(
  ({ conversation }: IConversationCalendlyAlertProps) => {
    const { recent_message } = conversation
    const { integrationsStore, eventLogStore, organizationStore } = useEntitiesContext()
    const calendlyIntegration = integrationsStore.getIntegration(IntegrationKey.calendly)
    const dismissDate = useLocalStorage(CALENDLY_ALERT_DISMISS_KEY)

    const isCalendlyIntegrationConnected = calendlyIntegration && calendlyIntegration.isConnected
    const isMessageIncludesCalendlyLink =
      recent_message?.user_id && recent_message.body?.includes('calendly.com')
    const isShouldShowAlert = isCalendlyIntegrationConnected || isMessageIncludesCalendlyLink

    const shouldShowAlert = () => {
      if (!isShouldShowAlert) {
        return false
      }
      if (!dismissDate) {
        return true
      }

      const dismissTimestamp = new Date(dismissDate).getTime()
      const oneMonthInMs = 30 * 24 * 60 * 60 * 1000

      return Date.now() - dismissTimestamp > oneMonthInMs
    }

    const handleDismiss = () => {
      setLocalStorageItem(CALENDLY_ALERT_DISMISS_KEY, new Date().toISOString())
    }

    const handleSetUpAgentClick = () => {
      eventLogStore.logEvent(
        'Calendly Banner Used',
        {
          event_id: 'calendly_banner_used',
          action: 'Booking Agent Requested',
          agencyId: organizationStore.agency_id,
        },
        { groupId: organizationStore.id }
      )

      uiStore.changeRouteViaVue({
        path: `/${ChatbotPath.all}`,
      })
    }

    if (!shouldShowAlert()) return null

    return (
      <Alert
        item={{
          type: 'infoStars',
          title: 'Let AI Book More Meetings—Automatically',
          desc: 'Streamline your scheduling with AI Scheduler, your always-on Booking Agent. Just connect your calendar, and it will handle coordination, confirmations, reschedules, and follow-ups—so you can focus on closing, not chasing.',
        }}
        actions={[
          {
            type: 'primary',
            variant: 'button',
            text: 'Set up AI Scheduler',
            onClick: handleSetUpAgentClick,
          },
          {
            type: 'secondary',
            variant: 'button',
            text: 'Don’t show again',
            onClick: handleDismiss,
          },
        ]}
      />
    )
  }
)
